import { PLAID_TRANSACTION_CATEGORY_TAXONOMY } from "../../../../../utils/constants/plaidCategoryTaxonomy";
import { getInitialsIconSrc } from "../../../../../utils/images/iconUtils";
import { getLanguageContent } from "../../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../../utils/schemas/language/pages/personalFinancesSchema";

const PERSONAL_FINANCES_CONTENT = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
const recurringTypeLabels =
  PERSONAL_FINANCES_CONTENT.recurring.recurrent_type_labels;
const institutionCopy = PERSONAL_FINANCES_CONTENT.institutions;

export const RECURRENT_TYPE_LABELS = {
  BL: recurringTypeLabels.bill,
  SC: recurringTypeLabels.subscription,
  CC: recurringTypeLabels.payment,
  IC: recurringTypeLabels.income,
  TR: recurringTypeLabels.transfer,
  LP: recurringTypeLabels.loan_payment,
  FM: recurringTypeLabels.frequent_merchant,
  OT: recurringTypeLabels.other,
};

export const RECURRENT_TYPE_OPTIONS = [
  { value: "BL", label: RECURRENT_TYPE_LABELS.BL, flows: ["spending"] },
  { value: "SC", label: RECURRENT_TYPE_LABELS.SC, flows: ["spending"] },
  { value: "CC", label: RECURRENT_TYPE_LABELS.CC, flows: ["spending"] },
  { value: "IC", label: RECURRENT_TYPE_LABELS.IC, flows: ["income"] },
  {
    value: "TR",
    label: RECURRENT_TYPE_LABELS.TR,
    flows: ["spending", "income"],
  },
  { value: "LP", label: RECURRENT_TYPE_LABELS.LP, flows: ["spending"] },
  { value: "FM", label: RECURRENT_TYPE_LABELS.FM, flows: ["spending"] },
  {
    value: "OT",
    label: RECURRENT_TYPE_LABELS.OT,
    flows: ["spending", "income"],
  },
];

export const getRecurrentTypeClassName = (label) =>
  `mp-recurring-type-badge type-pill type-${label
    .toLowerCase()
    .replace(/\s+/g, "-")}`;

const getFrequencyLabels = () => {
  const frequency = PERSONAL_FINANCES_CONTENT.recurring.frequency_options;

  return {
    WEEKLY: frequency.weekly,
    BIWEEKLY: frequency.biweekly,
    SEMI_MONTHLY: frequency.semi_monthly,
    MONTHLY: frequency.monthly,
    ANNUALLY: frequency.annually,
    UNKNOWN: frequency.unknown,
  };
};

export const formatCurrency = (amount) => {
  const numericAmount = Number(amount || 0);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Math.abs(numericAmount));
};

export const getAccountLabel = (transaction) => {
  return [
    transaction.account_name || transaction.account,
    transaction.institution_name || transaction.institution,
    transaction.account_mask || transaction.last_four_digits
      ? `••${transaction.account_mask || transaction.last_four_digits}`
      : null,
  ]
    .filter(Boolean)
    .join(" | ");
};

export const getMerchantName = (transaction) => {
  return (
    transaction?.merchant || transaction?.merchant_name || transaction?.name
  );
};

export const formatAccountDisplayName = (name, account = {}) => {
  const accountTypes = PERSONAL_FINANCES_CONTENT.global.account_types;
  const rawName = String(name || "");
  const subtype = String(account.subtype || account.account_subtype || "")
    .toLowerCase()
    .replace(/_/g, " ");
  const accountType = String(account.type || account.account_type || "")
    .toLowerCase()
    .replace(/_/g, " ");
  const mask = account.mask || account.account_mask || account.last_four_digits;
  const suffix = mask ? ` ${mask}` : "";

  if (subtype.includes("checking")) {
    return `${accountTypes.depository.checking}${suffix}`;
  }

  if (subtype.includes("savings")) {
    return `${accountTypes.depository.savings}${suffix}`;
  }

  if (accountType.includes("credit") || rawName.match(/^credit account/i)) {
    return rawName.replace(/^credit account/i, accountTypes.credit);
  }

  if (rawName.match(/^checking account/i)) {
    return rawName.replace(/^checking account/i, accountTypes.depository.checking);
  }

  if (rawName.match(/^savings account/i)) {
    return rawName.replace(/^savings account/i, accountTypes.depository.savings);
  }

  return rawName;
};

export const formatTransactionDate = (date) => {
  if (!date) return "";

  const [year, month, day] = date.split("-");
  if (!year || !month || !day) return date;

  return `${month}/${day}/${year}`;
};

const getPlaidCategory = (categoryId) => {
  return PLAID_TRANSACTION_CATEGORY_TAXONOMY[categoryId] || null;
};

const getPlaidCategoryLabel = (categoryId) => {
  const plaidCategory = getPlaidCategory(categoryId);

  return plaidCategory?.primary_friendly || PERSONAL_FINANCES_CONTENT.global.uncategorized;
};

const getPlaidCategoryDescription = (categoryId) => {
  const plaidCategory = getPlaidCategory(categoryId);

  return plaidCategory?.description || "";
};

const getPlaidCategoryPrimary = (categoryId) => {
  const plaidCategory = getPlaidCategory(categoryId);

  return plaidCategory?.primary || "";
};

export const mapTransactionToRow = (transaction) => {
  const amount = Number(transaction.amount || 0);
  const date = transaction.date || transaction.authorized_date;
  const categoryId = transaction.category || "";

  return {
    uuid: transaction.uuid,
    date: formatTransactionDate(date),
    date_value: date,
    merchant:
      transaction.merchant_name ||
      transaction.name ||
      institutionCopy.unknown_merchant,
    merchant_icon: transaction.logo_url,
    category: getPlaidCategoryLabel(categoryId),
    category_code: categoryId,
    category_primary: getPlaidCategoryPrimary(categoryId),
    category_detailed: categoryId,
    category_value: categoryId,
    note: transaction.note || "",
    display_description: getPlaidCategoryDescription(categoryId),
    amount: formatCurrency(amount),
    amount_value: Math.abs(amount),
    amount_type: amount >= 0 ? "sp" : "in",
    pending: transaction.pending,
    tax_deductible: Boolean(transaction.tax_deductible),
    account_uuid: transaction.account_uuid,
    account_name: formatAccountDisplayName(transaction.account_name, transaction),
    account_mask: transaction.account_mask,
    account_subtype: transaction.account_subtype || transaction.subtype,
    account_type: transaction.account_type || transaction.type,
    institution_uuid: transaction.institution_uuid,
    institution_name: transaction.institution_name,
  };
};

export const getUniqueOptions = (rows, valueKey, labelKey = valueKey) => {
  const optionsByValue = new Map();

  rows.forEach((row) => {
    const value = row[valueKey];
    if (!value || optionsByValue.has(value)) return;

    optionsByValue.set(value, row[labelKey] || value);
  });

  return Array.from(optionsByValue.entries()).map(([value, label]) => ({
    value,
    label,
  }));
};

export const formatLabel = (value) => {
  if (!value) return "";

  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const mapRecurringTransactionToRow = (recurringTransaction) => {
  const frequencyLabels = getFrequencyLabels();
  const amount = Number(recurringTransaction.last_amount || 0);
  const date = recurringTransaction.predicted_next_date;
  const categoryId = recurringTransaction.category || "";
  const recurrentTypeCode = recurringTransaction.recurrent_type || "OT";
  const flow =
    recurringTransaction.stream_type === "INFLOW" ? "income" : "spending";

  return {
    uuid: recurringTransaction.uuid,
    recurring_uuid: recurringTransaction.uuid,
    stream_type: recurringTransaction.stream_type,
    institution: recurringTransaction.institution_name || "",
    institution_uuid: recurringTransaction.institution_uuid || "",
    account: formatAccountDisplayName(
      recurringTransaction.account_name,
      recurringTransaction,
    ),
    account_uuid: recurringTransaction.account_uuid || "",
    last_four_digits: recurringTransaction.account_mask || "",
    merchant:
      recurringTransaction.display_name ||
      recurringTransaction.merchant_name ||
      recurringTransaction.description ||
      institutionCopy.unknown_merchant,
    merchant_icon: recurringTransaction.logo_url,
    frequency:
      frequencyLabels[recurringTransaction.frequency] ||
      recurringTransaction.frequency ||
      frequencyLabels.UNKNOWN,
    date: formatTransactionDate(date),
    date_value: date,
    amount: formatCurrency(amount),
    amount_value: amount,
    amount_type: flow === "income" ? "in" : "sp",
    recurrent_type:
      RECURRENT_TYPE_LABELS[recurrentTypeCode] ||
      PERSONAL_FINANCES_CONTENT.global.other,
    recurrent_type_code: recurrentTypeCode,
    detected_recurrent_type_code: recurringTransaction.detected_recurrent_type,
    recurrent_type_override: recurringTransaction.recurrent_type_override,
    category: getPlaidCategoryLabel(categoryId),
    category_code: categoryId,
    category_primary: getPlaidCategoryPrimary(categoryId),
    category_detailed: categoryId,
    category_value: categoryId,
    display_description: getPlaidCategoryDescription(categoryId),
    note: recurringTransaction.note || "",
    flow,
  };
};

export const getInstitutionIconSrc = (institution) => {
  if (institution.logo) {
    return `data:image/png;base64,${institution.logo}`;
  }

  return getInitialsIconSrc(institution.name, institution.primary_color);
};

export const formatAccountSubtype = (subtype) => {
  if (!subtype) return institutionCopy.account_subtype_fallback;

  const accountTypes = PERSONAL_FINANCES_CONTENT.global.account_types;
  const normalizedSubtype = String(subtype).toLowerCase().replace(/_/g, " ");

  if (normalizedSubtype.includes("checking")) {
    return accountTypes.depository.checking;
  }

  if (normalizedSubtype.includes("savings")) {
    return accountTypes.depository.savings;
  }

  if (normalizedSubtype.includes("credit")) {
    return accountTypes.credit;
  }

  return subtype
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const GROUP_LABELS = {
  depository: institutionCopy.group_labels.depository,
  credit: institutionCopy.group_labels.credit,
  loan: institutionCopy.group_labels.loan,
  other: institutionCopy.group_labels.other,
};

export const getGroupLabel = (type) => {
  return GROUP_LABELS[type] || type;
};

export const getInstitutionAvailableCash = (institution) => {
  return Number(institution?.metrics?.available_cash || 0);
};

export const getCreditMetrics = (group) => {
  const limit = Number(group?.balances?.limit || 0);
  const spent = Number(group?.balances?.current || 0);
  const available = Number(group?.balances?.available || 0);

  // Rounding both the pre-calculated metrics and our fallback mathematical calculation
  const percentUsed =
    group?.metrics?.percent_used !== undefined &&
    group?.metrics?.percent_used !== null
      ? Math.round(Number(group.metrics.percent_used))
      : limit > 0
      ? Math.round((spent / limit) * 100)
      : 0;

  return {
    limit,
    spent,
    available,
    percentUsed,
  };
};

export const getUtilizationMessage = (percentage, scope = "card") => {
  const isHigh = percentage >= 30;
  const tooltips = PERSONAL_FINANCES_CONTENT.institutions.tooltips;

  const config = {
    card: {
      high: {
        title: tooltips.high_card_utilization.title,
        content: tooltips.high_card_utilization.description.replace(
          "{percentage}",
          percentage,
        ),
      },
      healthy: {
        title: tooltips.healthy_utilization.title,
        content: tooltips.healthy_utilization.description.replace(
          "{percentage}",
          percentage,
        ),
      },
    },
    global: {
      high: {
        title: tooltips.high_global_utilization.title,
        content: tooltips.high_global_utilization.description.replace(
          "{percentage}",
          percentage,
        ),
      },
      healthy: {
        title: tooltips.healthy_utilization.title,
        content: tooltips.healthy_utilization.description.replace(
          "{percentage}",
          percentage,
        ),
      },
    },
  };

  return config[scope][isHigh ? "high" : "healthy"];
};
